import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Segmented } from "./mailingUi";

export const PLACEHOLDERS = ['name', 'email', 'environment', 'environmentUrl', 'expiryDate', 'daysLeft', 'trialDays'];

const FIELDS = [
  { key: 'subject', rows: 0, required: true },
  { key: 'header', rows: 2 },
  { key: 'body', rows: 9, required: true },
  { key: 'buttonText', rows: 0 },
  { key: 'buttonUrl', rows: 0 },
  { key: 'outro', rows: 4 },
];

const LANGUAGES = [{ value: 'nl', label: 'Nederlands' }, { value: 'en', label: 'English' }];

/**
 * Editor for a bilingual mailing: { en: {subject, header, body, buttonText, buttonUrl, outro}, nl: {...} }
 */
const MailingContentEditor = ({ content, onChange, showTrialBlock = false, language, onLanguageChange, onReset }) => {
  const { t } = useTranslation();
  const [localLanguage, setLocalLanguage] = useState('nl');
  const activeLanguage = language || localLanguage;
  const setActiveLanguage = onLanguageChange || setLocalLanguage;
  const fieldRefs = useRef({});
  const lastField = useRef('body');

  const handleChange = (field, value) => {
    onChange({
      ...content,
      [activeLanguage]: { ...content[activeLanguage], [field]: value },
    });
  };

  // Insert a placeholder at the cursor of the field that was focused last
  const insertPlaceholder = (placeholder) => {
    const field = lastField.current;
    const element = fieldRefs.current[field];
    const current = content?.[activeLanguage]?.[field] || '';
    const token = `{{${placeholder}}}`;
    const start = element?.selectionStart ?? current.length;
    const end = element?.selectionEnd ?? current.length;
    handleChange(field, current.slice(0, start) + token + current.slice(end));
    requestAnimationFrame(() => {
      if (!element) return;
      element.focus();
      element.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const isFilled = (lang) => Boolean(content?.[lang]?.subject?.trim() && content?.[lang]?.body?.trim());

  return (
    <div className="mail-editor">
      <div className="mail-editor-bar">
        <Segmented
          ariaLabel={t('mailing.language')}
          value={activeLanguage}
          onChange={setActiveLanguage}
          options={LANGUAGES.map((lang) => ({
            value: lang.value,
            label: (
              <>
                <span className={`mail-seg-dot ${isFilled(lang.value) ? 'ok' : ''}`} title={isFilled(lang.value) ? t('mailing.language_complete') : t('mailing.language_incomplete')} />
                {lang.label}
              </>
            ),
          }))}
        />
        {onReset && (
          <button type="button" className="mail-link-btn" onClick={onReset}>
            <i className="icon-undo" /> {t('mailing.reset_default')}
          </button>
        )}
      </div>

      {FIELDS.map(({ key, rows, required }) => {
        const props = {
          id: `mailing-${key}`,
          className: 'form-control',
          value: content?.[activeLanguage]?.[key] || '',
          onChange: (e) => handleChange(key, e.target.value),
          onFocus: () => { lastField.current = key; },
          ref: (element) => { fieldRefs.current[key] = element; },
        };
        return (
          <div className="form-group" key={key}>
            <label htmlFor={`mailing-${key}`}>
              {t(`mailing.field_${key}`)}{required && <span className="mail-required"> *</span>}
            </label>
            {rows > 0
              ? <textarea rows={rows} {...props} />
              : <input type="text" placeholder={key === 'buttonUrl' ? '{{environmentUrl}}' : ''} {...props} />}
          </div>
        );
      })}

      <div className="mail-placeholders">
        <div className="mail-placeholders-title">{t('mailing.placeholders_insert')}</div>
        <div className="mail-placeholder-chips">
          {PLACEHOLDERS.map((placeholder) => (
            <button key={placeholder} type="button" className="mail-placeholder-chip" onMouseDown={(e) => e.preventDefault()} onClick={() => insertPlaceholder(placeholder)}>
              {`{{${placeholder}}}`}
            </button>
          ))}
        </div>
        <ul className="mail-tips">
          <li>{t('mailing.formatting_help')}</li>
          {showTrialBlock && <li>{t('mailing.trial_block_help')}</li>}
          <li>{t('mailing.language_fallback_help')}</li>
        </ul>
      </div>
    </div>
  );
};

export default MailingContentEditor;
